import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'store/slices/authSlice';
import { logoutUser } from 'api/authApi';
import { Button } from 'components/ui/button';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store';

interface ProfileCardProps {
  username?: string;
  displayName?: string;
  onClose?: () => void;
  onOpenSettings?: () => void;
}

const ProfileCard = ({
  username,
  displayName,
  onClose,
  onOpenSettings,
}: ProfileCardProps) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  const cardRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    try {
      await logoutUser();
      dispatch(logout());
      console.log('Logout success');
      window.location.reload();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  useEffect(() => {
    // Only add the click handler when the settings dialog is NOT open
    if (!onOpenSettings && onClose) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          cardRef.current &&
          !cardRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [onClose, onOpenSettings]);

  return (
    <div
      ref={cardRef}
      className="absolute top-10 right-0 bg-background rounded-xl shadow-lg mt-2 p-6 w-[300px] z-40"
    >
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center justify-center w-16 h-16 bg-primary text-white rounded-[50%] text-2xl font-medium overflow-hidden">
          {user?.imgUrl ? (
            <img
              src={user.imgUrl}
              alt={user.displayName?.charAt(0)}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML =
                  user.displayName?.charAt(0).toUpperCase() || '';
              }}
            />
          ) : (
            <span>{username?.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div>
          <div className="font-semibold text-xl text-text-primary">
            Hi, {displayName}
          </div>
        </div>
      </div>

      <div className="h-[1px] bg-gray-200 mb-6" />

      <div className="space-y-3">
        <Button
          variant="customPrimary"
          className="w-full justify-center py-2.5"
          onClick={onOpenSettings}
        >
          Manage account
        </Button>
        <Button
          variant="customSecondary"
          className="w-full justify-center py-2.5"
          onClick={handleLogout}
        >
          Logout
        </Button>
      </div>
    </div>
  );
};

export default ProfileCard;
